import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Share2, RefreshCw, Copy, Loader2 } from "lucide-react";
import api from "../lib/api";
import { catMeta } from "../lib/verdictly";
import VerdictResult from "../components/VerdictResult";
import { useRoastShare, ShareCardPortal } from "../hooks/useRoastShare";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script"); s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true); s.onerror = () => resolve(false); document.body.appendChild(s);
  });
}

export default function MyTasks() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState(null);
  const [selected, setSelected] = useState(null);
  const [retesting, setRetesting] = useState(false);
  const { share, busyId, shareRef, shareItem } = useRoastShare();

  const load = async () => {
    try { setTasks((await api.get("/tasks/mine")).data); } catch (e) { setTasks([]); }
  };
  useEffect(() => { load(); }, []);

  const openTask = async (t) => {
    try { setSelected((await api.get(`/tasks/${t.id}`)).data); }
    catch (e) { toast.error("Could not open"); }
  };

  const shareResult = (t) => {
    const m = catMeta(t.category);
    const before = t.before_headline;
    share({
      kind: before ? "beforeafter" : "verdict",
      category: t.category, card_variant: t.card_variant, accent: m.accent,
      headline: t.result.headline, before,
      reviews_count: t.result.reviews_count, verb: t.result.headline.verb,
      token: t.public_token,
    }, t.id);
  };

  const copyPublic = (t) => {
    navigator.clipboard?.writeText(`${window.location.origin}/result/${t.public_token}`)?.catch(() => {});
    toast.success("Public result link copied!");
  };

  const retest = async (t) => {
    setRetesting(true);
    try {
      const q = (await api.get(`/tasks/${t.id}/retest-quote`)).data;
      const co = (await api.post("/tasks/checkout", {
        category: t.category, content_type: t.content_type, content: t.content,
        package: q.package, retest_of: t.id,
      })).data;
      const finalize = async (payment) => {
        await api.post("/tasks/confirm", { draft_id: co.draft_id, ...payment });
        toast.success("Retest live! New panel matching now. 🔁");
        setSelected(null); load();
      };
      if (co.mock) return await finalize({});
      const ok = await loadRazorpay(); if (!ok) throw new Error("payment window");
      const rzp = new window.Razorpay({
        key: co.key_id, amount: co.amount_minor, currency: co.currency, order_id: co.order_id,
        name: "RoastLit", description: "Retest", prefill: { name: co.name, email: co.email }, theme: { color: "#ff4500" },
        handler: async (r) => { await finalize({ payment_id: r.razorpay_payment_id, order_id: r.razorpay_order_id, signature: r.razorpay_signature }); },
      });
      rzp.open();
    } catch (e) { toast.error(e.response?.data?.detail || "Retest failed"); }
    finally { setRetesting(false); }
  };

  return (
    <div data-testid="my-tasks">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold uppercase tracking-tighter text-4xl sm:text-5xl">My verdicts</h1>
          <p className="text-zinc-400 mt-2">Track your panels and reveal the truth.</p>
        </div>
        <button data-testid="new-task-btn" onClick={() => navigate("/app/create")} className="px-5 py-2.5 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors">New verdict</button>
      </div>

      {tasks === null ? <div className="text-zinc-400">Loading…</div> : tasks.length === 0 ? (
        <div data-testid="tasks-empty" className="rounded-xl border border-white/10 bg-zinc-900/50 p-10 text-center text-zinc-400">
          No verdicts yet. <button onClick={() => navigate("/app/create")} className="text-brand font-bold">Start your first one →</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {tasks.map((t) => {
            const m = catMeta(t.category); const Icon = m.icon;
            const done = t.status === "complete";
            const pct = Math.round(((t.reviews_count || 0) / t.reviewers_required) * 100);
            return (
              <button key={t.id} data-testid={`task-card-${t.id}`} onClick={() => openTask(t)}
                className="text-left rounded-xl border border-white/10 bg-zinc-900/60 p-5 hover:-translate-y-0.5 transition-transform">
                <div className="flex items-center justify-between mb-3">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-2.5 py-1 rounded-full" style={{ color: m.accent, background: m.soft }}>
                    <Icon size={13} /> {t.category_name}{t.retest_of && " · retest"}
                  </span>
                  {done ? <span className="font-heading font-bold text-lg" style={{ color: m.accent }}>{t.result?.headline?.display}</span>
                    : <span className="inline-flex items-center gap-1 text-xs text-zinc-500"><Clock size={12} /> {t.reviews_count}/{t.reviewers_required}</span>}
                </div>
                <p className="text-sm text-zinc-300 line-clamp-2">{t.content_type === "text" ? t.content : "🖼 Image submission"}</p>
                {!done && (
                  <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${Math.max(6, pct)}%`, background: m.accent }} />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent data-testid="task-detail-dialog" className="bg-zinc-950 border-white/10 max-h-[92vh] overflow-y-auto max-w-lg">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading uppercase tracking-tight text-2xl">{selected.category_emoji} {selected.category_name}{selected.retest_of ? " · Retest" : ""}</DialogTitle>
                <DialogDescription>{selected.status === "complete" ? "Your panel has spoken." : `${selected.reviews_count} of ${selected.reviewers_required} verdicts in — hang tight.`}</DialogDescription>
              </DialogHeader>

              {selected.status !== "complete" ? (
                <div data-testid="task-pending" className="rounded-xl border border-white/10 bg-zinc-900/50 p-8 text-center">
                  <Loader2 className="mx-auto text-brand animate-spin mb-3" size={28} />
                  <div className="text-zinc-300">Matching & collecting {selected.reviewers_required} verdicts…</div>
                  <div className="text-sm text-zinc-500 mt-1">{selected.reviews_count} in so far</div>
                </div>
              ) : (
                <>
                  <VerdictResult task={selected} result={selected.result} before={selected.before_headline} testPrefix="mytask" />
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <button data-testid="share-verdict-btn" onClick={() => shareResult(selected)} disabled={busyId === selected.id}
                      className="inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors disabled:opacity-50">
                      {busyId === selected.id ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />} Share card
                    </button>
                    <button data-testid="copy-public-btn" onClick={() => copyPublic(selected)}
                      className="inline-flex items-center justify-center gap-2 py-2.5 rounded-full border border-white/15 text-white hover:bg-white/5 transition-colors">
                      <Copy size={15} /> Copy link
                    </button>
                  </div>
                  <button data-testid="retest-btn" onClick={() => retest(selected)} disabled={retesting}
                    className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full border border-brand/40 bg-brand/10 text-brand font-bold hover:bg-brand/20 transition-colors disabled:opacity-50">
                    <RefreshCw size={15} /> {retesting ? "Setting up…" : "Made changes? Retest at 50% off"}
                  </button>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <ShareCardPortal shareRef={shareRef} item={shareItem} />
    </div>
  );
}
